/**
 * Utility functions for encrypting and decrypting sensitive data
 * Uses AES encryption from CryptoJS
 */

import CryptoJS from 'crypto-js';
import { error } from './logger';

// Get encryption key from environment variables or use default
// In a production environment, this should be stored securely
// and potentially derived from user-specific information
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || '';

/**
 * Encrypts data using AES encryption
 * @param data - The data to encrypt (object or string)
 * @returns Encrypted string
 */
export const encryptData = (data: any): string => {
  try {
    // Convert data to string if it's an object
    const dataString = typeof data === 'string' ? data : JSON.stringify(data);
    
    // Use CryptoJS AES encryption
    const encrypted = CryptoJS.AES.encrypt(dataString, ENCRYPTION_KEY).toString();
    
    return encrypted;
  } catch (err : any) {
    error('Encryption failed:', err);
    // Return original data if encryption fails
    return typeof data === 'string' ? data : JSON.stringify(data);
  }
};

/**
 * Decrypts data that was encrypted with encryptData
 * @param encryptedData - The encrypted string
 * @returns Decrypted data (parsed as JSON if possible)
 */
export const decryptData = (encryptedData: string): any => {
  try {
    // Use CryptoJS AES decryption
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    
    // Try to parse as JSON if possible
    try {
      return JSON.parse(decrypted);
    } catch {
      return decrypted;
    }
  } catch (err : any) {
    error('Decryption failed:', err);
    // Return original data if decryption fails
    return encryptedData;
  }
};

/**
 * Safely gets encrypted data from localStorage
 * @param key - The localStorage key
 * @returns Decrypted data
 */
export const getEncryptedFromStorage = (key: string): any => {
  const encryptedData = localStorage.getItem(key);
  if (!encryptedData) return null;
  
  return decryptData(encryptedData);
};

/**
 * Safely stores encrypted data in localStorage
 * @param key - The localStorage key
 * @param data - The data to encrypt and store
 */
export const setEncryptedInStorage = (key: string, data: any): void => {
  const encryptedData = encryptData(data);
  localStorage.setItem(key, encryptedData);
};

/**
 * Convenience function to get organization trials data
 * @returns Decrypted organization trials data or null if not found
 */
export const getOrganizationTrials = (): any => {
  return getEncryptedFromStorage('organization_trials');
};

/**
 * Convenience function to get user data
 * @returns Decrypted user data or null if not found
 */
export const getUserData = (): any => {
  // console.log("User data: " + JSON.stringify(getEncryptedFromStorage('user')));

  return getEncryptedFromStorage('user');
};

/**
 * Convenience function to set user data
 * @param userData - The user data to encrypt and store
 */
export const setUserData = (userData: any): void => {
  setEncryptedInStorage('user', userData);
};


/**
 * Convenience function to get organization trials data
 * @returns Decrypted organization trials data or null if not found
*/
export const setOrganizationTrials = (orgData: any): any => {
  setEncryptedInStorage('organization_trials', orgData);
};